import React, {
  createContext,
  FormEvent,
  SyntheticEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { metaSymbol, metaTrigger } from '@support/keyboardHandler';
import { fetchContact, selectContact, updateContactSucceeded } from '@actions/contacts';
import Avatar from './Avatar';
import Tip from './Tip';
import Container from '@components/ContentContainer';
import Header from '@components/Header';
import FormBuilder from '@components/FormBuilder';
import buildFormData from '@support/buildFormData';
import { CheckIcon, ClearProfileIcon, LoaderIcon } from '@components/Icons';
import contactStyles from '../Contact.module.scss';
import styles from './ContactForm.module.scss';
import Section from '@components/Section';
import StateBasedFadeTransition from '@components/transitions/StateBasedFadeTransition';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuthContext } from '@hooks/use-auth-context';
import analytics from '@support/analytics';
import { useHotkeyHandler } from '@hooks/use-hotkey-handler';
import { useNavigationConfirmation } from '@hooks/use-navigation-confirmation';
import Contact from '@models/Contact';
import { apiRequest } from '@support/api';
import TabNavigation from '../TabNavigation';
import RefreshProfile from './RefreshProfile';
import { SourcesListEditor } from '@components/Contact/Sources/SourcesListEditor';
import { fetchSources } from '@actions/sources';

export type AvatarConfig = {
  key: 'avatar_url';
  type: 'gallery';
  options?: Array<string>;
  value?: string;
};

export const ContactFormContext = createContext<{ contactIsDeceased: boolean }>({
  contactIsDeceased: false,
});

export default function ContactForm() {
  const { user, contactIsRestricted } = useAuthContext();
  const { id: contactID } = useParams();
  const isSelf = contactID == 'self' || Number(user.contact_id) === Number(contactID || -1);
  const dispatchToStore = useDispatch();
  const navigate = useNavigate();
  const contact = useSelector(selectContact(isSelf ? String(user.contact_id) : contactID));

  const [loading, setLoading] = useState(true);
  const [clear, setClear] = useState(false);
  const [avatar, setAvatar] = useState<AvatarConfig>(null);
  const [form, setForm] = useState([]);
  // Would be better to type this as a Contact, but we would first want to switch
  // to only camel cased property names, or handle casing as part of our request flow
  const [data, setData] = useState<any>({});
  const [sections, setSections] = useState([]);
  const [sectionIndex, setSectionIndex] = useState(0);
  const [upload, setUpload] = useState(null);
  // This is gross as hell, but we need to make sure that all calls to navigate aren't memoized. They
  // had been getting memoized in a submitForm promise resolution -- sometimes nav would fail
  const isMounted = useRef(true);
  const [shouldExit, setShouldExit] = useState(false);

  const formDeceasedStatus = form?.[0]?.fields.find(({ key }) => key === 'is_memorialized')?.value;
  const formBirthdayDate = form?.[0]?.fields.find(({ key }) => key === 'birthday')?.value;
  const formDeceasedDate = form?.[0]?.fields.find(({ key }) => key === 'memorialized_at')?.value;

  const handleClear = (e) => {
    e.preventDefault();

    window.app.confirm(
      {
        title: 'Are you sure you want to clear this profile?',
        message:
          'This change cannot be undone. You will be clearing out all profile information including any custom edits. Your notes and reminders will remain.',
        action: 'Clear Profile',
        variant: 'danger',
      },
      (confirmed) => {
        if (!confirmed) {
          return;
        }

        setClear(true);
        setData({});

        apiRequest<Contact>('POST', `/network/contacts/${contactID}/form/`, { clear: true })
          .then((response) => {
            analytics.track('Contact Cleared', {
              contactId: isSelf ? 'self' : contactID,
            });
            dispatchToStore(updateContactSucceeded(response));
          })
          .catch((err) => {
            console.error(err);
          });
      },
    );
  };

  // warning: mutates input...
  const processFormSection = (section) => {
    for (const field of section.fields) {
      if (field.type !== 'repeater') {
        continue;
      }

      if (field.data.length > 0) {
        continue;
      }

      field.data.push({});
    }
  };

  // warning: mutates input...
  const processForm = (form) => {
    let avatar = {};
    const sections = [];

    let i = 0;
    for (const item of form) {
      if (item.type === 'section') {
        sections.push({ index: i, title: item.label });
        processFormSection(item);
      }

      i++;
      if (item.label !== 'Profile') {
        continue;
      }

      for (const field of item.fields) {
        if (field.key === 'avatar_url') {
          avatar = field;
          break;
        }
      }
    }

    return { form, avatar, sections };
  };

  const handleChange = (key, value) => {
    const updatedData = { ...data, [key]: value };

    if (key === 'is_memorialized' && value === true) {
      return setData({ ...updatedData, send_birthday_notification: false });
    }

    if (key === 'is_memorialized' && value === false) {
      return setData({
        ...updatedData,
        send_memorialized_notification: false,
        memorialized_at: { month: null, day: null, year: null },
      });
    }

    if (
      key === 'send_birthday_notification' &&
      value === true &&
      (!formBirthdayDate?.month || !formBirthdayDate?.day)
    ) {
      const today = new Date();
      const unsavedBirthdayDate = data.birthday;
      const birthday = {
        month: unsavedBirthdayDate?.month || formBirthdayDate?.month || today.getMonth() + 1,
        day: unsavedBirthdayDate?.day || formBirthdayDate?.day || today.getDate(),
        year: unsavedBirthdayDate?.year || formBirthdayDate?.year,
      };
      return setData({ ...updatedData, birthday });
    }

    if (
      key === 'send_memorialized_notification' &&
      value === true &&
      (!formDeceasedDate?.month || !formDeceasedDate?.day)
    ) {
      const today = new Date();
      const unsavedDeceasedDate = data.memorialized_at;
      const memorialized_at = {
        month: unsavedDeceasedDate?.month || formDeceasedDate?.month || today.getMonth() + 1,
        day: unsavedDeceasedDate?.day || formDeceasedDate?.day || today.getDate(),
        year: unsavedDeceasedDate?.year || formDeceasedDate?.year,
      };
      return setData({ ...updatedData, memorialized_at });
    }

    setData(updatedData);
  };

  const handleAvatar = (url, upload = null) => {
    setData({ ...data, avatar_url: url });
    setUpload(upload);
  };

  const processFormData = (data) => {
    const formData = { ...data };
    if (formData.avatar_url && formData.avatar_url.startsWith('blob:')) {
      formData.avatar_url = upload;
    }

    for (const [key, value] of Object.entries(formData)) {
      if (!Array.isArray(value)) {
        continue;
      }

      formData[key] = value.filter((item) => {
        let entries = Object.entries(item);

        if (entries.length === 0) {
          return false;
        }

        entries = entries.filter(([_, value]) => typeof value === 'string' && value.length > 0);

        return entries.length > 0;
      });
    }
    return formData;
  };

  const submitForm = (e?: SyntheticEvent | KeyboardEvent): Promise<void> => {
    if (e) {
      e.preventDefault();
    }

    if (loading) {
      // FIXME I'm not sure this is a good thing to do?
      //  It returns a never-resolving Promise? Should it be Promise.resolve()?
      return new Promise(() => {});
    }

    setLoading(true);
    const formData = processFormData(data);
    const body = buildFormData(formData);

    return apiRequest<Contact>('POST', `/network/contacts/${contactID}/form/`, body)
      .then((response) => {
        analytics.track('Contact Edited', { contactId: isSelf ? 'self' : contactID });
        dispatchToStore(updateContactSucceeded(response));
      })
      .catch((err) => {
        setLoading(false);
        console.error(err);
      });
  };

  const saveAndExit = (e: KeyboardEvent | FormEvent) => {
    // We nav back 2 locations because there is an extra location pushed onto the stack
    // from useNavigationConfirmation
    submitForm(e).then(() => isMounted.current && setShouldExit(true));
  };

  const renderHeaderActions = () => {
    const disabled = clear ? false : Object.keys(data).length === 0;

    return (
      <StateBasedFadeTransition
        state={loading ? 'loading' : 'loaded'}
        elementByStateMap={{
          loading: (
            <Header.Action large IconComponent={() => <LoaderIcon width="16" height="16" />} />
          ),
          loaded: (
            <Header.Action
              large
              disabled={disabled}
              className="btn-none"
              iconClassName={styles.headerBtn}
              as="button"
              type="submit"
              IconComponent={CheckIcon}
              tooltip={!disabled}
              shortcut={[metaSymbol, 'enter']}
              title="Save and Exit"
            />
          ),
        }}
      />
    );
  };

  let displayName = contact.displayName;
  if (data.first_name || data.last_name) {
    displayName = `${data.first_name || contact.firstName || ''} ${
      data.last_name || contact.lastName || ''
    }`.trim();
  }

  const renderHeader = () => {
    return (
      <Header goBack actions={renderHeaderActions()}>
        Editing {isSelf ? 'Your Profile' : displayName}
      </Header>
    );
  };

  const getForm = () => {
    setLoading(true);
    apiRequest('GET', `/network/contacts/${contactID}/form`, { query: { version: 'v3.1' } })
      .then((resp) => {
        if (!isMounted.current) {
          return;
        }
        const { form, avatar, sections } = processForm(resp);
        setForm(form);
        setAvatar(avatar as AvatarConfig);
        setSections(sections);
        setData({});
        setLoading(false);
      })
      .catch((err) => {
        if (!isMounted.current) {
          return;
        }

        setLoading(false);
        console.error(err);
      });
  };

  useEffect(() => {
    if (isSelf) {
      dispatchToStore(fetchContact({ id: user.contact_id }));
      dispatchToStore(fetchSources({ contact: { id: user.contact_id } }));
    } else if (!contact.isLoaded) {
      dispatchToStore(fetchContact({ id: contactID }));
      dispatchToStore(fetchSources({ contact: { id: contactID } }));
    }

    getForm();
  }, []);

  useEffect(() => {
    getForm();
  }, [contact.timestamp]);

  // If user tries to hit a profile edit view for a restricted contact, redirect them back
  useEffect(() => {
    if (contact && contact.isPersisted && contactIsRestricted(contact)) {
      navigate(-1);
    }
  }, [contact]);

  useEffect(() => {
    if (shouldExit && isMounted.current) {
      navigate(-2);
    }
  }, [shouldExit, navigate]);

  useEffect(
    () => () => {
      isMounted.current = false;
    },
    [],
  );

  useNavigationConfirmation(
    {
      hasUnsavedChanges: !loading && Object.keys(data).length > 0,
      saveChanges: submitForm,
    },
    [data, loading],
  );

  useHotkeyHandler(
    {
      handler: (e) => {
        if (e.key == 'Enter' && metaTrigger(e)) {
          saveAndExit(e);
        }
      },
    },
    [data, loading],
  );

  const contactIsDeceased = data.hasOwnProperty('is_memorialized')
    ? data.is_memorialized
    : formDeceasedStatus;

  return (
    <ContactFormContext.Provider value={{ contactIsDeceased }}>
      <Container as="form" onSubmit={saveAndExit}>
        <Container.Primary header={renderHeader()} className={contactStyles.container}>
          <div className={contactStyles.content}>
            {form.length ? (
              <Avatar
                value={data.avatar_url}
                avatar={avatar}
                displayName={displayName}
                initials={displayName ? displayName[0] : ''}
                onChange={handleAvatar}
                grayscale={contactIsDeceased}
              />
            ) : null}
            <TabNavigation
              tabs={sections}
              activeTabIdx={sectionIndex}
              setActiveTabIdx={setSectionIndex}
            />
            {form.length ? <Tip /> : null}
            {!form[sectionIndex] ? null : (
              <div className="form">
                {form[sectionIndex].fields.map((field, i) => (
                  <FormBuilder
                    cleared={clear}
                    key={i}
                    item={field}
                    data={data}
                    onChange={handleChange}
                  />
                ))}
              </div>
            )}
          </div>
        </Container.Primary>
        <Container.Sidebar>
          <div className={contactStyles.sidebar}>
            <Section>
              <Section.Title underline>Manage Profile</Section.Title>
              <RefreshProfile contact={contact} />
              <button disabled={clear} className={styles.clearProfileBtn} onClick={handleClear}>
                Clear Profile
                <ClearProfileIcon />
              </button>
            </Section>
            <SourcesListEditor contact={contact} />
          </div>
        </Container.Sidebar>
      </Container>
    </ContactFormContext.Provider>
  );
}
